import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { fetchLedger } from '../../services/backendAPI';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const Ledger = () => {
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLedgerData();
  }, []);

  const fetchLedgerData = async () => {
    try {
      setLoading(true);
      const response = await fetchLedger();
      if (response?.data) {
        setLedgerData(response.data);
      }
    } catch (error) {
      console.error('Error fetching ledger data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (row) => {
    navigate('/ledger/details', { state: { ledgerData: row } });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 2, 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: '100%',
      position: 'relative',
      pb: 10 // Add padding bottom to ensure pagination is visible
    }}>
      <Typography variant="h5" gutterBottom sx={{ flexShrink: 0 }}>
        Ledger
      </Typography>
      <Paper sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative'
      }}>
        <TableContainer sx={{ 
          maxHeight: 'calc(100vh - 250px)',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: '4px',
          },
        }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Debit</TableCell>
                <TableCell>Credit</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ledgerData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {format(new Date(row.date), 'dd/MM/yyyy')}
                    </TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.debit || '-'}</TableCell>
                    <TableCell>{row.credit || '-'}</TableCell>
                    <TableCell>{row.balance}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleViewDetails(row)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 1000,
          backgroundColor: 'background.paper',
          width: '100%'
        }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={ledgerData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              width: '100%',
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default Ledger; 